import { Koi } from '@ksyos/koi';
import {
    GestoordeDonkerAdaptie,
    OorkRedenKeuring,
    RefractieOptimaleCorrectie,
    RijbewijsCategorie,
} from '@common/interfaces/ophthalmology/OorkAndOcrk/oorkV1ProcedureCustomControlData';
import { onePhoneNumberRequiredSchema } from '@common/validation/schemas';
import { emailSchema } from '@common/validation/schemas/emailSchema';
import { ophthalmologyFile } from '../ophthalmology';
import { ocrkV1DraftRequest, ocrkV1FinalRequest } from './ocrk';
import { afwijkingenSchema } from './sharedSchemas';

const eigen_correctie_draft = Koi.object().keys({
    bril: Koi.boolean(),
    contactlenzen_overdag: Koi.boolean(),
    geen_eigen_correctie: Koi.boolean(),
    nachtlenzen: Koi.boolean(),
});

const eigen_correctie_final = eigen_correctie_draft.anyFilledIn();

export const oorkV1FinalProcedure = Koi.object()
    .keys({
        aanwijzingen_defecten_gezichtsveld: Koi.boolean(),
        beschrijving_gezichtsvelddefecten: Koi.when('aanwijzingen_defecten_gezichtsveld', {
            is: true,
            otherwise: Koi.valid(null),
            then: Koi.string().max(1000).allow(null),
        }),
        betrokkene_bezit_rijbewijs: Koi.boolean(),
        betrokkene_gebruikt_hulpmiddel: Koi.when('sprake_van_diplopie', {
            is: true,
            otherwise: Koi.valid(null),
            then: Koi.boolean(),
        }),
        bsn_betrokkene: Koi.string().elfproef(),
        categorie_rijbewijs: Koi.koi().enum(RijbewijsCategorie),
        consult: ocrkV1FinalRequest,
        datum_onderzoek: Koi.string(),
        diplopie_is_hinderlijk: Koi.when('sprake_van_diplopie', {
            is: true,
            otherwise: Koi.valid(null),
            then: Koi.boolean(),
        }),
        eigen_correctie: eigen_correctie_final,
        email_betrokkene: emailSchema,
        files: Koi.array().items(ophthalmologyFile).max(24).allow(null),
        gestoorde_donkeradaptie: Koi.koi().enum(GestoordeDonkerAdaptie),
        inspection_rights_consented: Koi.boolean().invalid(false),
        legitimatie_betrokkene_gezien: Koi.boolean(),
        overige_anamnese: Koi.string().max(1000).allow(null),
        overige_reden_van_keuring: Koi.when('reden_van_keuring', {
            is: OorkRedenKeuring.overig,
            otherwise: Koi.valid(null),
            then: Koi.string().max(1000),
        }),
        reden_van_keuring: Koi.koi().enum(OorkRedenKeuring),
        refractie_optimale_correctie: Koi.koi().enum(RefractieOptimaleCorrectie),
        share_medical_info_consented: Koi.boolean().invalid(false),
        sprake_van_diplopie: Koi.boolean(),
        toelichting_diplopie: Koi.when('sprake_van_diplopie', {
            is: true,
            otherwise: Koi.valid(null),
            then: Koi.string().max(1000).allow(null),
        }),
        toelichting_donkeradaptie: Koi.string().max(1000).allow(null),
        visus_eigen_correctie_od: Koi.string().max(50).allow(null),
        visus_eigen_correctie_ods: Koi.string().max(50).allow(null),
        visus_eigen_correctie_os: Koi.string().max(50).allow(null),
        visus_optimale_correctie_od: Koi.string().max(50),
        visus_optimale_correctie_ods: Koi.string().max(50),
        visus_optimale_correctie_os: Koi.string().max(50),
        visus_zonder_correctie_od: Koi.string().max(50),
        visus_zonder_correctie_ods: Koi.string().max(50),
        visus_zonder_correctie_os: Koi.string().max(50),
        zorgdomein_code: Koi.string().max(15),
    })
    .concat(onePhoneNumberRequiredSchema('homePhoneNumber', 'mobileNumber'));

export const oorkV1DraftProcedure = oorkV1FinalProcedure.keys({
    aanwijzingen_defecten_gezichtsveld: Koi.boolean().allow(null),
    betrokkene_bezit_rijbewijs: Koi.boolean().allow(null),
    betrokkene_gebruikt_hulpmiddel: Koi.when('sprake_van_diplopie', {
        is: true,
        otherwise: Koi.valid(null),
        then: Koi.boolean().allow(null),
    }),
    bsn_betrokkene: Koi.string().elfproef().allow(null),
    categorie_rijbewijs: Koi.koi().enum(RijbewijsCategorie).allow(null),
    consult: ocrkV1DraftRequest,
    datum_onderzoek: Koi.string().allow(null),
    diplopie_is_hinderlijk: Koi.when('sprake_van_diplopie', {
        is: true,
        otherwise: Koi.valid(null),
        then: Koi.boolean().allow(null),
    }),
    eigen_correctie: eigen_correctie_draft,
    email_betrokkene: emailSchema.allow(null),
    files: Koi.array().items(ophthalmologyFile).max(24).allow(null),
    gestoorde_donkeradaptie: Koi.koi().enum(GestoordeDonkerAdaptie).allow(null),
    homePhoneNumber: Koi.string().max(1000).allow(null, '').optional(),
    inspection_rights_consented: Koi.boolean().allow(null),
    legitimatie_betrokkene_gezien: Koi.boolean().allow(null),
    mobileNumber: Koi.string().max(1000).allow(null, '').optional(),
    overige_reden_van_keuring: Koi.when('reden_van_keuring', {
        is: OorkRedenKeuring.overig,
        otherwise: Koi.valid(null),
        then: Koi.string().max(1000).allow(null),
    }),
    reden_van_keuring: Koi.koi().enum(OorkRedenKeuring).allow(null),
    refractie_optimale_correctie: Koi.koi().enum(RefractieOptimaleCorrectie).allow(null),
    share_medical_info_consented: Koi.boolean().allow(null),
    sprake_van_diplopie: Koi.boolean().allow(null),
    visus_optimale_correctie_od: Koi.string().max(50).allow(null),
    visus_optimale_correctie_ods: Koi.string().max(50).allow(null),
    visus_optimale_correctie_os: Koi.string().max(50).allow(null),
    visus_zonder_correctie_od: Koi.string().max(50).allow(null),
    visus_zonder_correctie_ods: Koi.string().max(50).allow(null),
    visus_zonder_correctie_os: Koi.string().max(50).allow(null),
    zorgdomein_code: Koi.string().max(15).allow(null),
});

const oorkV1BasesDiagnosticReport = Koi.object().keys({
    aanvullende_informatie: Koi.string().max(1000).allow(null),
    afwijkingen_gevonden: Koi.boolean(),
    relevante_comorbiditeit: Koi.boolean(),
    toelichting_relevante_comorbiditeit: Koi.when('relevante_comorbiditeit', {
        is: true,
        otherwise: Koi.valid(null),
        then: Koi.string().max(1000).allow(null),
    }),
    toelichting_verworven_nystagmus: Koi.when('afwijkingen.verworven_nystagmus', {
        is: true,
        otherwise: Koi.valid(null),
        then: Koi.string().max(1000).allow(null),
    }),
});

export const oorkV1FinalDiagnosticReport = oorkV1BasesDiagnosticReport.concat(
    Koi.object().keys({
        afwijkingen: Koi.when('afwijkingen_gevonden', {
            is: true,
            otherwise: afwijkingenSchema,
            then: afwijkingenSchema.anyFilledIn(),
        }),
    })
);

export const oorkV1DraftDiagnosticReport = oorkV1BasesDiagnosticReport.concat(
    Koi.object().keys({
        afwijkingen: afwijkingenSchema,
        afwijkingen_gevonden: Koi.boolean().allow(null),
        relevante_comorbiditeit: Koi.boolean().allow(null),
    })
);
